import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Toast } from 'primereact/toast';

import apiClient from '../../stateManagers/services/axiosInterceptor';
import CustomPaginator from '../../components/CustomPaginator';
import TeachersListSkeletonLoader from '../../skeletons/TeachersListSkeletonLoader';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import PasswordResetModal from '../Students/parts/PasswordResetModal';
import ViewStaffModal from './parts/ViewStaffModal';
import RegisterStaffModal from './parts/RegisterStaffModal';
import PanelAccessModal from './parts/PanelAccessModal';
import { useRole } from '../../stateManagers/roleAccess/useRole';

interface StaffData {
  _id: string;
  user_id: string;
  name: string;
  gender: string;
  email_id: string;
  parentage: string;
  address: string;
  contact_no: string;
  qualification: string;
  marital_status: string;
  job_profile: string;
  account_no: string;
  bank_name: string;
  ifsc_code: string;
  created_at: string;
  updated_at: string;
  profile_picture_url?: string;
  user_status?: number;
  status?: string;
}

interface PaginationData {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
}

interface GenderStatistics {
  counts: {
    Male: number;
    Female: number;
    Other: number;
  };
  total: number;
  percentages: {
    Male: number;
    Female: number;
    Other: number;
  };
}

type ViewMode = 'card' | 'list';

const BLUE = '#4169E1';
const BLUE_LIGHT = '#6b8df5';

const StaffList: React.FC = () => {
  const { isAdmin } = useRole();   // ← add this near the top
  const [staffList, setStaffList] = useState<StaffData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [imageErrors, setImageErrors] = useState<Map<string, boolean>>(new Map());
  const [statusLoadingIds, setStatusLoadingIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [listKey, setListKey] = useState<number>(0);
  const [genderStats, setGenderStats] = useState<GenderStatistics | null>(null);

  const [paginationData, setPaginationData] = useState<PaginationData>({
    current_page: 1,
    per_page: 12,
    total_items: 0,
    total_pages: 1,
  });

  const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
  const [registerModalOpen, setRegisterModalOpen] = useState<boolean>(false);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [selectedStaffData, setSelectedStaffData] = useState<StaffData | null>(null);

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordResetTarget, setPasswordResetTarget] = useState<StaffData | null>(null);

  const [panelAccessModalOpen, setPanelAccessModalOpen] = useState(false);
  const [panelAccessStaff, setPanelAccessStaff] = useState<StaffData | null>(null);

  const menuRefs = useRef<{ [key: string]: Menu | null }>({});
  const toast = useRef<Toast>(null);

  useEffect(() => { fetchStaff(); }, [currentPage]);

  const fetchStaff = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await apiClient.get('/staff', {
        params: { 
          page: currentPage, 
          limit: paginationData.per_page 
        },
      });

      const data = response.data;
      if (data.status === 'success') {
        setStaffList(data.data);
        setPaginationData({
          current_page: data.pagination.current_page,
          per_page: data.pagination.per_page,
          total_items: data.pagination.total_items,
          total_pages: data.pagination.total_pages,
        });
        
        if (data.gender_statistics) {
          setGenderStats(data.gender_statistics);
        }
        
        setListKey(k => k + 1);
      }
    } catch (err: any) {
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Error', 
        detail: err.response?.data?.message || 'Failed to fetch staff', 
        life: 4000 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = () => { setSelectedStaffData(null); setRegisterModalOpen(true); };
  
  const handleView = (s: StaffData) => {
  console.log('Staff _id:', s._id);
  setSelectedStaffId(s._id);
  setViewModalOpen(true);
};
  
  const handleEdit = (s: StaffData) => {
    const staffWithId = { ...s, id: s._id };
    setSelectedStaffData(staffWithId);
    setRegisterModalOpen(true);
  };
  
  const handleUploadPhoto = (s: StaffData) => {
    const staffWithId = { ...s, id: s._id };
    setSelectedStaffData(staffWithId);
    setRegisterModalOpen(true);
  };
  
  const handleOpenPasswordReset = (s: StaffData) => { 
    setPasswordResetTarget(s); 
    setIsPasswordModalOpen(true); 
  };
  
  const handlePanelAccess = (s: StaffData) => {
    const staffWithId = { ...s, id: s._id };
    setPanelAccessStaff(staffWithId);
    setPanelAccessModalOpen(true);
  };

  const handleRegistrationSuccess = (shouldResetPage?: boolean) => {
    if (shouldResetPage) setCurrentPage(1); 
    else fetchStaff();
  };



  const handleToggleStatus = async (staff: StaffData) => {
    const newStatus = staff.user_status === 1 ? 0 : 1;
    const action = newStatus === 1 ? 'Activate' : 'Deactivate';
    setStatusLoadingIds(prev => new Set(prev).add(staff.user_id));
    
    try {
      const response = await apiClient.patch(`/staff/${staff._id}/status`, { 
        status: newStatus === 1 ? 'active' : 'deactivated' 
      });
      
      if (response.data.status === 'success') {
        setStaffList(prev => prev.map(s => 
          s.user_id === staff.user_id ? { ...s, user_status: newStatus } : s
        ));
        toast.current?.show({
          severity: newStatus === 1 ? 'success' : 'warn',
          summary: `${action}d`,
          detail: `${staff.name} has been ${action.toLowerCase()}d successfully.`,
          life: 3000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error', 
        summary: 'Error',
        detail: err?.response?.data?.message ?? err?.message ?? `Failed to ${action.toLowerCase()} staff`,
        life: 4000,
      });
    } finally {
      setStatusLoadingIds(prev => { const n = new Set(prev); n.delete(staff.user_id); return n; });
    }
  };

  const getMenuItems = (staff: StaffData): MenuItem[] => {
  const isActive = staff.user_status !== 0;
  const isStatusLoading = statusLoadingIds.has(staff.user_id);

  const items: MenuItem[] = [
    { label: 'View Details',   icon: 'pi pi-eye',     command: () => handleView(staff) },
    { label: 'Edit',           icon: 'pi pi-pencil',  command: () => handleEdit(staff) },
    { label: 'Upload Photo',   icon: 'pi pi-camera',  command: () => handleUploadPhoto(staff) },
    { label: 'Reset Password', icon: 'pi pi-key',     command: () => handleOpenPasswordReset(staff), className: 'text-violet-600' },
  ];

  // only admin sees this
  if (isAdmin) {
    items.push({
      label: 'Panel Access',
      icon: 'pi pi-shield',
      command: () => handlePanelAccess(staff),
      className: 'text-blue-600',
    });
  }

  items.push(
    { separator: true },
    {
      label: isStatusLoading
        ? (isActive ? 'Deactivating…' : 'Activating…')
        : (isActive ? 'Deactivate' : 'Activate'),
      icon: isStatusLoading
        ? 'pi pi-spin pi-spinner'
        : (isActive ? 'pi pi-ban' : 'pi pi-check-circle'),
      command: () => { if (!isStatusLoading) handleToggleStatus(staff); },
      className: isActive ? 'text-red-500' : 'text-green-500',
    },
  );

  return items;
};

  const Avatar = ({ staff, size = 88 }: { staff: StaffData; size?: number }) => {
    const hasErr = imageErrors.get(staff.user_id) || false;
    const isActive = staff.user_status !== 0;
    const fontSize = size > 50 ? 30 : 15;
    
    return staff.profile_picture_url && !hasErr ? (
      <img
        src={staff.profile_picture_url} 
        alt={staff.name}
        onError={() => setImageErrors(prev => new Map(prev).set(staff.user_id, true))}
        style={{
          width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
          border: `${size > 50 ? 3 : 2}px solid ${isActive ? 'rgba(65,105,225,0.2)' : 'rgba(239,68,68,0.2)'}`,
          boxShadow: isActive ? '0 4px 14px rgba(65,105,225,0.18)' : '0 4px 14px rgba(0,0,0,0.1)',
          filter: isActive ? 'none' : 'grayscale(55%) opacity(0.72)',
          transition: 'filter 0.4s ease',
        }}
      />
    ) : (
      <div style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: isActive
          ? `linear-gradient(145deg,${BLUE},${BLUE_LIGHT})`
          : 'linear-gradient(145deg,#b0b8c9,#d1d5db)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize, fontWeight: 700, color: 'white',
        border: `${size > 50 ? 3 : 2}px solid ${isActive ? 'rgba(65,105,225,0.2)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: isActive ? '0 4px 14px rgba(65,105,225,0.25)' : '0 4px 14px rgba(0,0,0,0.1)',
      }}>
        {staff.name.charAt(0).toUpperCase()}
      </div>
    );
  };

  const StatusPill = ({ staff, small = false }: { staff: StaffData; small?: boolean }) => {
    const isActive = staff.user_status !== 0;
    const isLoading = statusLoadingIds.has(staff.user_id);
    
    return (
      <div
        className="status-pill"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: small ? '3px 10px' : '5px 13px',
          borderRadius: 30, userSelect: 'none', cursor: isLoading ? 'default' : 'pointer',
          transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
          background: isLoading ? 'rgba(245,158,11,0.09)' : isActive ? 'rgba(16,185,129,0.09)' : 'rgba(239,68,68,0.09)',
          border: `1px solid ${isLoading ? 'rgba(245,158,11,0.28)' : isActive ? 'rgba(16,185,129,0.28)' : 'rgba(239,68,68,0.28)'}`,
        }}
        onClick={() => { if (!isLoading) handleToggleStatus(staff); }}
        title={isLoading ? 'Processing...' : isActive ? 'Click to deactivate' : 'Click to activate'}
      >
        {isLoading
          ? <i className="pi pi-spin pi-spinner" style={{ fontSize: 9, color: '#f59e0b' }} />
          : <div className={isActive ? 'dot-pulse' : ''} style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? '#10b981' : '#ef4444' }} />
        }
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
          transition: 'color 0.4s ease',
          color: isLoading ? '#f59e0b' : isActive ? '#10b981' : '#ef4444',
        }}>
          {isLoading ? (isActive ? 'Deactivating...' : 'Activating...') : isActive ? 'Active' : 'Inactive'}
        </span>
      </div>
    );
  };

  const StaffCard = ({ staff, index }: { staff: StaffData; index: number }) => {
    const isActive = staff.user_status !== 0;
    const isStatusLoading = statusLoadingIds.has(staff.user_id);

    return (
      <div
        className="staff-card"
        style={{
          '--delay': `${index * 0.048}s`,
          borderRadius: 22,
          border: `1.5px solid ${isActive ? '#eaecf2' : 'rgba(239,68,68,0.18)'}`,
          boxShadow: isActive
            ? '0 1px 6px rgba(65,105,225,0.06), 0 4px 18px rgba(0,0,0,0.05)'
            : '0 1px 6px rgba(239,68,68,0.06), 0 4px 18px rgba(0,0,0,0.04)',
          transition: 'box-shadow 0.45s cubic-bezier(0.22,1,0.36,1), transform 0.45s cubic-bezier(0.22,1,0.36,1), border-color 0.35s ease',
          height: '100%',
          background: isActive
            ? 'linear-gradient(160deg,#ffffff 60%,#f5f8ff 100%)'
            : 'linear-gradient(160deg,#fffafa 60%,#fff5f5 100%)',
          position: 'relative', overflow: 'hidden',
        } as React.CSSProperties}
        onMouseEnter={e => {
          const el = e.currentTarget;
          el.style.boxShadow = isActive
            ? '0 10px 36px rgba(65,105,225,0.15), 0 2px 8px rgba(65,105,225,0.08)'
            : '0 10px 36px rgba(239,68,68,0.12), 0 2px 8px rgba(239,68,68,0.07)';
          el.style.transform = 'translateY(-6px) scale(1.01)';
          el.style.borderColor = isActive ? 'rgba(65,105,225,0.28)' : 'rgba(239,68,68,0.3)';
        }}
        onMouseLeave={e => {
          const el = e.currentTarget;
          el.style.boxShadow = isActive
            ? '0 1px 6px rgba(65,105,225,0.06), 0 4px 18px rgba(0,0,0,0.05)'
            : '0 1px 6px rgba(239,68,68,0.06), 0 4px 18px rgba(0,0,0,0.04)';
          el.style.transform = 'translateY(0) scale(1)';
          el.style.borderColor = isActive ? '#eaecf2' : 'rgba(239,68,68,0.18)';
        }}
      >
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 120, pointerEvents: 'none',
          background: isActive
            ? 'linear-gradient(160deg,rgba(65,105,225,0.05) 0%,rgba(91,124,230,0.025) 60%,transparent 100%)'
            : 'linear-gradient(160deg,rgba(239,68,68,0.04) 0%,rgba(239,68,68,0.018) 60%,transparent 100%)',
          borderRadius: '22px 22px 60% 60%/22px 22px 28px 28px',
        }} />
        <div style={{
          position: 'absolute', top: -28, right: -28, width: 90, height: 90,
          borderRadius: '50%', pointerEvents: 'none',
          background: isActive
            ? 'radial-gradient(circle,rgba(65,105,225,0.07) 0%,transparent 70%)'
            : 'radial-gradient(circle,rgba(239,68,68,0.06) 0%,transparent 70%)',
        }} />

        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
          <Button
            icon="pi pi-ellipsis-v" rounded text severity="secondary"
            onClick={e => menuRefs.current[staff._id]?.toggle(e)}
            style={{
              width: 30, height: 30, color: '#9ca3af',
              background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(10px)',
              border: '1px solid rgba(0,0,0,0.06)', borderRadius: 10,
              fontSize: 11, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          />
          <Menu
            model={getMenuItems(staff)} popup
            ref={el => { if (el) menuRefs.current[staff._id] = el; }}
            style={{ width: 190, borderRadius: 14 }}
          />
        </div>

        <div style={{
          position: 'absolute', top: 12, left: 12, zIndex: 10,
          display: 'flex', alignItems: 'center', gap: 4, padding: '4px 9px',
          borderRadius: 30, background: 'rgba(255,255,255,0.88)',
          border: '1px solid rgba(0,0,0,0.07)', backdropFilter: 'blur(10px)',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          <i className="pi pi-id-card" style={{ fontSize: 9, color: '#9ca3af' }} />
          <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, letterSpacing: '0.03em' }}>
            #{staff._id.slice(-6)}
          </span>
        </div>

        <div style={{ padding: '50px 22px 22px', textAlign: 'center', position: 'relative' }}>
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: 14 }}>
            <div style={{
              position: 'absolute', inset: -5, borderRadius: '50%', pointerEvents: 'none',
              background: isActive
                ? 'radial-gradient(circle,rgba(65,105,225,0.13) 0%,transparent 70%)'
                : 'radial-gradient(circle,rgba(239,68,68,0.10) 0%,transparent 70%)',
            }} />
            <Avatar staff={staff} size={88} />
            <div
              className={isStatusLoading ? 'dot-spin' : isActive ? 'dot-pulse' : ''}
              style={{
                position: 'absolute', bottom: 3, right: 3,
                width: 15, height: 15, borderRadius: '50%',
                background: isStatusLoading
                  ? 'linear-gradient(135deg,#f59e0b,#fbbf24)'
                  : isActive
                    ? 'linear-gradient(135deg,#10b981,#34d399)'
                    : 'linear-gradient(135deg,#ef4444,#f87171)',
                border: '2.5px solid white',
                boxShadow: isActive && !isStatusLoading
                  ? '0 2px 6px rgba(16,185,129,0.45)'
                  : '0 2px 6px rgba(239,68,68,0.4)',
                transition: 'background 0.5s ease',
              }}
            />
          </div>

          <h3 style={{
            margin: '0 0 7px', fontSize: 16, fontWeight: 700,
            letterSpacing: '-0.025em', lineHeight: 1.25,
            color: isActive ? '#1e2940' : '#a0aab8', transition: 'color 0.4s ease',
          }}>
            {staff.name}
          </h3>

          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 11px', borderRadius: 30, marginBottom: 10,
            background: 'linear-gradient(135deg,rgba(65,105,225,0.09),rgba(91,124,230,0.06))',
            border: '1px solid rgba(65,105,225,0.13)',
          }}>
            <div style={{
              width: 5, height: 5, borderRadius: '50%',
              background: BLUE, boxShadow: '0 0 5px rgba(65,105,225,0.5)',
            }} />
            <span style={{ fontSize: 11, color: BLUE, fontWeight: 600, letterSpacing: '0.025em' }}>
              {staff.job_profile || 'Staff'}
            </span>
          </div>

          <div style={{ marginBottom: 16 }}>
            <StatusPill staff={staff} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, paddingTop: 15, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
            {([
              { icon: 'pi pi-envelope', value: staff.email_id,   mono: false },
              { icon: 'pi pi-phone',    value: staff.contact_no,  mono: true },
            ] as const).map(({ icon, value, mono }) => (
              <div
                key={icon}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 11px', borderRadius: 12,
                  background: 'rgba(248,249,252,0.85)', border: '1px solid rgba(0,0,0,0.04)',
                  transition: 'all 0.28s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(65,105,225,0.05)';
                  e.currentTarget.style.borderColor = 'rgba(65,105,225,0.18)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(248,249,252,0.85)';
                  e.currentTarget.style.borderColor = 'rgba(0,0,0,0.04)';
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: 'linear-gradient(145deg,rgba(65,105,225,0.1),rgba(91,124,230,0.07))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className={icon} style={{ fontSize: 11, color: BLUE }} />
                </div>
                <span style={{
                  flex: 1, textAlign: 'left', fontWeight: 500, fontSize: 12, color: '#4b5563',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontFamily: mono ? 'monospace' : 'inherit',
                }}>
                  {value}
                </span>
              </div>
            ))}

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 11px', borderRadius: 12,
              background: 'rgba(248,249,252,0.85)', border: '1px solid rgba(0,0,0,0.04)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="pi pi-user" style={{ fontSize: 11, color: '#9ca3af' }} />
                <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>User ID</span>
              </div>
              <span style={{
                fontSize: 11, fontWeight: 700, color: BLUE, fontFamily: 'monospace',
                padding: '2px 9px', borderRadius: 7,
                background: 'rgba(65,105,225,0.08)', letterSpacing: '0.02em',
              }}>
                #{staff.user_id.slice(-6)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const serialTemplate = (_: StaffData, opts: ColumnBodyOptions) => {
    const n = opts.rowIndex + (currentPage - 1) * paginationData.per_page + 1;
    return (
      <div style={{
        width: 28, height: 28, background: '#f9fafb', borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 12, color: '#6b7280', margin: '0 auto',
      }}>{n}</div>
    );
  };

  const avatarNameTemplate = (s: StaffData) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Avatar staff={s} size={38} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {s.name}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>{s.gender}</div>
      </div>
    </div>
  );

  const jobProfileTemplate = (s: StaffData) => (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 20,
      background: 'rgba(65,105,225,0.08)', border: '1px solid rgba(65,105,225,0.13)',
    }}>
      <span style={{ fontSize: 11, color: BLUE, fontWeight: 600 }}>{s.job_profile || 'Staff'}</span>
    </div>
  );

  const emailTemplate = (s: StaffData) => (
    <span style={{ fontSize: 12, color: '#4b5563', fontWeight: 500 }}>{s.email_id}</span>
  );

  const phoneTemplate = (s: StaffData) => (
    <span style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace', fontWeight: 500 }}>{s.contact_no}</span>
  );

  const statusTemplate = (s: StaffData) => <StatusPill staff={s} small />;

  const actionTemplate = (s: StaffData) => (
    <div style={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
      {[
        { icon: 'pi pi-eye',    color: '#60a5fa', tip: 'View',           fn: () => handleView(s) },
        { icon: 'pi pi-pencil', color: '#3b82f6', tip: 'Edit',           fn: () => handleEdit(s) },
        ...(isAdmin
          ? [{ icon: 'pi pi-shield', color: BLUE, tip: 'Panel Access', fn: () => handlePanelAccess(s) }]
          : []),
        { icon: 'pi pi-key',    color: '#8b5cf6', tip: 'Reset Password', fn: () => handleOpenPasswordReset(s) },
      ].map(({ icon, color, tip, fn }) => (
        <Button
          key={icon}
          icon={icon}
          className="p-button-text p-button-rounded"
          tooltip={tip}
          tooltipOptions={{ position: 'top' }}
          style={{ color, width: 32, height: 32 }}
          onClick={fn}
        />
      ))}
    </div>
  );

  const GenderStatsSummary = () => {
    if (!genderStats) return null;
    
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        flexWrap: 'wrap', padding: '10px 16px',
        borderRadius: 12, background: 'rgba(248,249,252,0.85)',
        border: '1px solid rgba(0,0,0,0.04)',
      }}>
        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
          Gender Stats:
        </span>
        <div style={{ display: 'flex', gap: 12 }}>
          <span style={{ fontSize: 12, color: '#3b82f6', fontWeight: 500 }}>
            ♂ {genderStats.counts.Male} ({genderStats.percentages.Male.toFixed(1)}%)
          </span>
          <span style={{ fontSize: 12, color: '#ec4899', fontWeight: 500 }}>
            ♀ {genderStats.counts.Female} ({genderStats.percentages.Female.toFixed(1)}%)
          </span>
          <span style={{ fontSize: 12, color: '#8b5cf6', fontWeight: 500 }}>
            ⚧ {genderStats.counts.Other} ({genderStats.percentages.Other.toFixed(1)}%)
          </span>
          <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
            Total: {genderStats.total}
          </span>
        </div>
      </div>
    );
  };

  const emptyContent = (
    <div style={{
      background: 'linear-gradient(160deg,#ffffff 60%,#f5f8ff 100%)',
      borderRadius: 22, padding: '90px 20px', textAlign: 'center',
      boxShadow: '0 2px 16px rgba(0,0,0,0.05)', border: '1.5px solid rgba(65,105,225,0.1)',
    }}>
      <div style={{
        width: 60, height: 60, borderRadius: 16, margin: '0 auto 22px',
        background: `linear-gradient(145deg,${BLUE},${BLUE_LIGHT})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 6px 20px rgba(65,105,225,0.25)',
      }}>
        <i className="pi pi-briefcase" style={{ fontSize: 26, color: 'white' }} />
      </div>
      <h3 style={{ margin: '0 0 10px', color: BLUE, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>
        No Staff Members Found
      </h3>
      <p style={{ margin: '0 auto', color: '#94a3b8', fontSize: 14, lineHeight: 1.65, maxWidth: 360 }}>
        Start by adding your first staff member using the button above.
      </p>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(18px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes rowIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0);    }
        }

        .staff-card {
          animation: cardIn 0.52s cubic-bezier(0.22,1,0.36,1) both;
          animation-delay: var(--delay, 0s);
          will-change: transform, opacity;
        }

        @keyframes softPulse {
          0%,100% { opacity:1;   transform:scale(1);    }
          50%      { opacity:0.7; transform:scale(0.85); }
        }
        @keyframes dotSpin { to { transform:rotate(360deg); } }
        .dot-pulse { animation: softPulse 2.6s ease-in-out infinite; }
        .dot-spin  { animation: dotSpin   1s   linear       infinite; }

        .status-pill:hover { filter:brightness(1.05); transform:translateY(-1px); }

        .p-datatable .p-datatable-tbody > tr {
          animation: rowIn 0.32s ease-out both;
          transition: background 0.15s;
        }
        .p-datatable .p-datatable-tbody > tr:hover {
          background: rgba(65,105,225,0.04) !important;
          box-shadow: inset 4px 0 0 0 ${BLUE};
        }
        .p-datatable .p-datatable-thead > tr > th {
          background: #f9fafb !important; color: #6b7280 !important;
          font-size: 11px !important; font-weight: 700 !important;
          text-transform: uppercase !important; letter-spacing: .8px !important;
          padding: 16px 12px !important; border-bottom: 1px solid #e5e7eb !important;
        }
        .p-datatable .p-datatable-tbody > tr > td {
          padding: 14px 12px !important; border-bottom: 1px solid #f3f4f6 !important;
        }
        .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom: none !important; }
        .p-button-text:hover { background: #f3f4f6 !important; }

        .p-menu {
          border-radius: 14px !important; padding: 6px !important;
          border: 1px solid rgba(0,0,0,0.07) !important;
          box-shadow: 0 8px 28px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.06) !important;
        }
        .p-menu .p-menuitem-link {
          padding: 8px 11px !important; border-radius: 9px !important;
          margin: 2px 0 !important; transition: all 0.2s ease !important;
        }
        .p-menu .p-menuitem-link:hover  { background: rgba(65,105,225,0.06) !important; }
        .p-menu .p-menuitem-icon        { color: #8492a6 !important; font-size: 12px !important; margin-right: 9px !important; }
        .p-menu .p-menuitem-text        { font-size: 13px !important; font-weight: 500 !important; color: #374151 !important; }
        .p-menu .p-menuitem-separator   { margin: 5px 4px !important; border-top: 1px solid rgba(0,0,0,0.06) !important; }

        .text-red-500    .p-menuitem-link { color: #ef4444 !important; }
        .text-red-500    .p-menuitem-link:hover { background: rgba(239,68,68,0.07) !important; }
        .text-red-500    .p-menuitem-icon,
        .text-red-500    .p-menuitem-text  { color: #ef4444 !important; }

        .text-green-500  .p-menuitem-link { color: #10b981 !important; }
        .text-green-500  .p-menuitem-link:hover { background: rgba(16,185,129,0.07) !important; }
        .text-green-500  .p-menuitem-icon,
        .text-green-500  .p-menuitem-text  { color: #10b981 !important; }

        .text-violet-600 .p-menuitem-link { color: #7c3aed !important; }
        .text-violet-600 .p-menuitem-link:hover { background: rgba(124,58,237,0.07) !important; }
        .text-violet-600 .p-menuitem-icon,
        .text-violet-600 .p-menuitem-text  { color: #7c3aed !important; }

        .text-blue-600   .p-menuitem-link { color: ${BLUE} !important; }
        .text-blue-600   .p-menuitem-link:hover { background: rgba(65,105,225,0.07) !important; }
        .text-blue-600   .p-menuitem-icon,
        .text-blue-600   .p-menuitem-text  { color: ${BLUE} !important; }

        .view-toggle-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 14px; border-radius: 10px; font-size: 12px;
          font-weight: 600; cursor: pointer; border: 1.5px solid;
          transition: all 0.22s cubic-bezier(0.22,1,0.36,1);
          background: none;
        }
        .view-toggle-btn.active   { background: ${BLUE}; color: white; border-color: ${BLUE}; box-shadow: 0 3px 10px rgba(65,105,225,0.28); }
        .view-toggle-btn.inactive { background: white; color: ${BLUE}; border-color: #c7d2fe; }
        .view-toggle-btn.inactive:hover { background: #eff6ff; }

        @media (max-width:1280px) { .staff-grid { grid-template-columns: repeat(3,1fr) !important; } }
        @media (max-width:900px)  { .staff-grid { grid-template-columns: repeat(2,1fr) !important; } }
        @media (max-width:560px)  { .staff-grid { grid-template-columns: 1fr !important; } }
      `}</style>

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <GlobalHeaderTitle
          title="Staff Management"
          subtitle={`${paginationData.total_items} total staff members`}
          icon="pi pi-briefcase"
          buttons={[{ label: 'Add Staff', icon: 'pi pi-plus', onClick: handleAddStaff }]}
        />

        {!loading && genderStats && <GenderStatsSummary />}

        {loading ? (
          <TeachersListSkeletonLoader />
        ) : staffList.length > 0 ? (
          <>
            <div style={{
              borderRadius: 14, padding: '14px 18px', marginBottom: 20, marginTop: 16,
              boxShadow: '0 1px 6px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.06)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexWrap: 'wrap', gap: 10,
              background: 'linear-gradient(135deg,#ffffff,#f9fbff)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 10,
                  background: 'linear-gradient(145deg,rgba(65,105,225,0.1),rgba(91,124,230,0.07))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 1px 4px rgba(65,105,225,0.12)',
                }}>
                  <i className="pi pi-list" style={{ fontSize: 14, color: BLUE }} />
                </div>
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                  Showing{' '}
                  <span style={{ color: '#1e2940' }}>
                    {((currentPage - 1) * paginationData.per_page) + 1}–{Math.min(currentPage * paginationData.per_page, paginationData.total_items)}
                  </span>
                  {' '}of{' '}
                  <span style={{ color: '#1e2940' }}>{paginationData.total_items}</span> staff members
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className={`view-toggle-btn ${viewMode === 'card' ? 'active' : 'inactive'}`} onClick={() => setViewMode('card')}>
                    <i className="pi pi-th-large" style={{ fontSize: 12 }} /> Cards
                  </button>
                  <button className={`view-toggle-btn ${viewMode === 'list' ? 'active' : 'inactive'}`} onClick={() => setViewMode('list')}>
                    <i className="pi pi-list" style={{ fontSize: 12 }} /> List
                  </button>
                </div>
                <div style={{
                  padding: '6px 14px', borderRadius: 10,
                  background: 'linear-gradient(135deg,rgba(65,105,225,0.09),rgba(91,124,230,0.06))',
                  border: '1px solid rgba(65,105,225,0.13)',
                }}>
                  <span style={{ fontSize: 12, color: BLUE, fontWeight: 700 }}>
                    Page {paginationData.current_page} / {paginationData.total_pages}
                  </span>
                </div>
              </div>
            </div>

            {viewMode === 'card' && (
              <div
                key={`cards-${listKey}`}
                className="staff-grid"
                style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20, marginBottom: 32 }}
              >
                {staffList.map((s, i) => <StaffCard key={s._id} staff={s} index={i} />)}
              </div>
            )}

            {viewMode === 'list' && (
              <div
                key={`list-${listKey}`}
                style={{
                  background: 'white', borderRadius: 20, marginBottom: 32,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                }}
              >
                <DataTable value={staffList} loading={false} emptyMessage="">
                  <Column header="#"           body={serialTemplate}     style={{ width: 52 }} />
                  <Column header="Name"        body={avatarNameTemplate}  style={{ minWidth: 180 }} />
                  <Column header="Job Profile" body={jobProfileTemplate}  style={{ width: 150 }} />
                  <Column header="Email"       body={emailTemplate}       style={{ minWidth: 160 }} />
                  <Column header="Phone"       body={phoneTemplate}       style={{ width: 140 }} />
                  <Column header="Status"      body={statusTemplate}      style={{ width: 130 }} />
                  <Column header="Actions"     body={actionTemplate}      style={{ width: 170 }} />
                </DataTable>
              </div>
            )}

            {paginationData.total_pages > 1 && (
              <CustomPaginator
                page={paginationData.current_page}
                totalPages={paginationData.total_pages}
                totalRecords={paginationData.total_items}
                pageSize={paginationData.per_page}
                onPageChange={newPage => setCurrentPage(newPage)}
                showInfoBar={false}
              />
            )}
          </>
        ) : emptyContent}
      </div>

      <ViewStaffModal
        open={viewModalOpen}
        onClose={() => { setViewModalOpen(false); setSelectedStaffId(null); }}
        staffId={selectedStaffId}
      />
      <RegisterStaffModal
        open={registerModalOpen}
        onClose={() => { setRegisterModalOpen(false); setSelectedStaffData(null); }}
        onSuccess={handleRegistrationSuccess}
        staffData={selectedStaffData}
      />
      <PasswordResetModal
  visible={isPasswordModalOpen}
  onClose={() => { 
    setIsPasswordModalOpen(false); 
    setPasswordResetTarget(null); 
  }}
  studentName={passwordResetTarget?.name ?? ''}
  userId={passwordResetTarget?.user_id ?? ''}
/>
      <PanelAccessModal
        open={panelAccessModalOpen}
        onClose={() => { setPanelAccessModalOpen(false); setPanelAccessStaff(null); }}
        staffData={panelAccessStaff}
      />
    </>
  );
};

export default StaffList;